/** Утилиты дат для расписания (сетка — Europe/Moscow, подписи зрителя — опционально). */

export const WEEKDAY_NAMES = [
  'Пн',
  'Вт',
  'Ср',
  'Чт',
  'Пт',
  'Сб',
  'Вс',
];

export const WEEKDAY_FULL = [
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
  'воскресенье',
];

export const TEACHER_TZ = 'Europe/Moscow';
export const TEACHER_TZ_SHORT = 'МСК';

export const VIEWER_TZ_STORAGE_KEY = 'diobuch.schedule.viewerTz';
export const VIEWER_TZ_BROWSER = 'browser';

/** Короткий список городов РФ + «как в браузере». */
export const VIEWER_TZ_OPTIONS = [
  { value: VIEWER_TZ_BROWSER, label: 'Как в браузере' },
  { value: 'Europe/Kaliningrad', label: 'Калининград', short: 'Клг' },
  { value: 'Europe/Moscow', label: 'Москва', short: 'МСК' },
  { value: 'Europe/Samara', label: 'Самара', short: 'Смр' },
  { value: 'Asia/Yekaterinburg', label: 'Екатеринбург', short: 'Екб' },
  { value: 'Asia/Omsk', label: 'Омск', short: 'Омск' },
  { value: 'Asia/Krasnoyarsk', label: 'Красноярск', short: 'Крс' },
  { value: 'Asia/Irkutsk', label: 'Иркутск', short: 'Ирк' },
  { value: 'Asia/Yakutsk', label: 'Якутск', short: 'Як' },
  { value: 'Asia/Vladivostok', label: 'Владивосток', short: 'Влд' },
];

export function moscowDateISO(d = new Date()) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TEACHER_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

export function parseISODate(iso) {
  const [y, m, day] = iso.split('-').map(Number);
  return new Date(y, m - 1, day);
}

export function addDaysISO(iso, days) {
  const d = parseISODate(iso);
  d.setDate(d.getDate() + days);
  return formatLocalISO(d);
}

export function formatLocalISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function mondayOf(iso) {
  const d = parseISODate(iso);
  const wd = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - wd);
  return formatLocalISO(d);
}

export function weekDates(mondayIso) {
  return Array.from({ length: 7 }, (_, i) => addDaysISO(mondayIso, i));
}

export function formatTimeFromISO(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TEACHER_TZ,
  });
}

export function formatDateShort(isoDate) {
  const d = parseISODate(isoDate);
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
}

export function minutesFromMidnight(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + m;
}

export function timeFromOccurrenceStart(iso) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: TEACHER_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(new Date(iso));
  const hour = parts.find((p) => p.type === 'hour')?.value || '00';
  const minute = parts.find((p) => p.type === 'minute')?.value || '00';
  return `${hour}:${minute}`;
}

export function occurrenceDateMoscow(iso) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: TEACHER_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || TEACHER_TZ;
  } catch {
    return TEACHER_TZ;
  }
}

export function loadViewerTzPreference() {
  try {
    const raw = localStorage.getItem(VIEWER_TZ_STORAGE_KEY);
    if (!raw) return TEACHER_TZ;
    if (raw === VIEWER_TZ_BROWSER) return VIEWER_TZ_BROWSER;
    if (VIEWER_TZ_OPTIONS.some((o) => o.value === raw)) return raw;
  } catch {
    /* ignore */
  }
  return TEACHER_TZ;
}

export function saveViewerTzPreference(value) {
  try {
    localStorage.setItem(VIEWER_TZ_STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
}

/** Резолвит preference → IANA timezone. */
export function resolveViewerTimezone(preference) {
  if (!preference || preference === TEACHER_TZ) return TEACHER_TZ;
  if (preference === VIEWER_TZ_BROWSER) return getBrowserTimezone();
  return preference;
}

export function timezoneShortLabel(ianaTz) {
  if (!ianaTz || ianaTz === TEACHER_TZ) return TEACHER_TZ_SHORT;
  const known = VIEWER_TZ_OPTIONS.find((o) => o.value === ianaTz);
  if (known?.short) return known.short;
  // браузерный неизвестный — короткое имя из IANA
  const parts = String(ianaTz).split('/');
  return parts[parts.length - 1] || ianaTz;
}

function formatPartsInTz(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  const weekdayEn = get('weekday'); // Mon, Tue...
  const wdMap = { Mon: 'пн', Tue: 'вт', Wed: 'ср', Thu: 'чт', Fri: 'пт', Sat: 'сб', Sun: 'вс' };
  return {
    hour: get('hour') || '00',
    minute: get('minute') || '00',
    dateISO: `${get('year')}-${get('month')}-${get('day')}`,
    weekdayShort: wdMap[weekdayEn] || '',
  };
}

export function formatTimeInTz(dateOrIso, timeZone) {
  const d = dateOrIso instanceof Date ? dateOrIso : new Date(dateOrIso);
  const p = formatPartsInTz(d, timeZone);
  return `${p.hour}:${p.minute}`;
}

/**
 * Двойная подпись: «16:00» или «16:00 МСК · 18:00» / «16:00 МСК · 00:00 (чт)».
 * @param {string|Date} isoOrDate
 * @param {string} viewerTzPreference — raw preference (может быть 'browser')
 */
export function formatDualTime(isoOrDate, viewerTzPreference) {
  const d = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return '';
  const msk = formatPartsInTz(d, TEACHER_TZ);
  const mskTime = `${msk.hour}:${msk.minute}`;
  const viewerTz = resolveViewerTimezone(viewerTzPreference);
  if (viewerTz === TEACHER_TZ) return mskTime;

  const local = formatPartsInTz(d, viewerTz);
  const localTime = `${local.hour}:${local.minute}`;
  const dayShift = local.dateISO !== msk.dateISO;
  const short = timezoneShortLabel(viewerTz);
  if (dayShift && local.weekdayShort) {
    return `${mskTime} ${TEACHER_TZ_SHORT} · ${localTime} (${local.weekdayShort})`;
  }
  return `${mskTime} ${TEACHER_TZ_SHORT} · ${localTime}`;
}

/**
 * Для шаблонного HH:MM в weekday (0=пн): якорим на ближайший такой день в МСК.
 */
export function formatDualTimeFromTemplate(hhmm, weekday, viewerTzPreference) {
  const viewerTz = resolveViewerTimezone(viewerTzPreference);
  const time = (hhmm || '00:00').slice(0, 5);
  if (viewerTz === TEACHER_TZ) return time;

  const todayIso = moscowDateISO();
  const today = parseISODate(todayIso);
  const todayWd = (today.getDay() + 6) % 7;
  const delta = (weekday - todayWd + 7) % 7;
  const targetIso = addDaysISO(todayIso, delta);
  return formatDualTimeOnDate(targetIso, time, viewerTzPreference);
}

/** HH:MM на конкретной дате (МСК) → dual подпись. */
export function formatDualTimeOnDate(isoDate, hhmm, viewerTzPreference) {
  const time = (hhmm || '00:00').slice(0, 5);
  const viewerTz = resolveViewerTimezone(viewerTzPreference);
  if (viewerTz === TEACHER_TZ) return time;
  const [y, m, day] = String(isoDate).split('-').map(Number);
  const [h, mi] = time.split(':').map(Number);
  // Europe/Moscow = UTC+3 constantly
  const utcMs = Date.UTC(y, m - 1, day, h - 3, mi, 0);
  return formatDualTime(new Date(utcMs), viewerTzPreference);
}

/** Текст часов в тулбаре. */
export function formatNowClockLabel(now = new Date(), viewerTzPreference = TEACHER_TZ) {
  const msk = formatTimeInTz(now, TEACHER_TZ);
  const viewerTz = resolveViewerTimezone(viewerTzPreference);
  if (viewerTz === TEACHER_TZ) {
    return `Сейчас ${msk} ${TEACHER_TZ_SHORT}`;
  }
  const local = formatTimeInTz(now, viewerTz);
  const short = timezoneShortLabel(viewerTz);
  return `Сейчас ${msk} ${TEACHER_TZ_SHORT} · ${local} ${short}`;
}

/**
 * Позиция линии «сейчас» в пикселях от верха колонки (МСК).
 * null если вне рабочего окна или сегодня не в отображаемой неделе.
 */
export function nowLineTopPx({
  now = new Date(),
  workStart,
  workEnd,
  hourHeight,
  todayIso = null,
}) {
  const today = todayIso || moscowDateISO(now);
  if (moscowDateISO(now) !== today) return null;

  const nowMin = minutesFromMidnight(formatTimeInTz(now, TEACHER_TZ));
  const startMin = minutesFromMidnight((workStart || '11:00').slice(0, 5));
  const endMin = minutesFromMidnight((workEnd || '21:00').slice(0, 5));
  if (nowMin < startMin || nowMin > endMin) return null;
  return ((nowMin - startMin) / 60) * hourHeight;
}
